import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * 「自分が回答済みの survey id 集合」を返す helper hook (Sprint T、user 第 16 弾)。
 *
 * 各 survey に対して `getDoc(districts/{id}/surveys/{sid}/responses/{uid})` を Promise.all で
 * 並列取得 → 存在する surveyId を Set で返す。
 *
 * collectionGroup query を使う方法 (1 query で済む) もあるが、Firestore composite index が
 * 必要かつ rules で許可するために collectionGroup 用 rules を別途記述する必要があるため、
 * 本 sprint では doc 並列読み込み方式で minimum 実装 (件数が少ない想定、1 地区あたり数 surveys)。
 *
 * subscribe は行わない (一覧 modal を開いた時の snapshot 表示用、submit 後の再評価は別経路で
 * `useSurveyResponse` 経由で個別 survey に対して reactive に取得される)。
 *
 * R-70 P3b: `selfUids` (現 UID + 端末引き継ぎ元 UID 群) を渡すと、いずれかの UID で回答済みなら
 * 「回答済」と判定する (引き継ぎ後の二重回答防止)。response doc ID = uid なので、各 survey × 各 UID を
 * 並列 read し、1 つでも存在すれば回答済とする。rules は isLinkedOwner で引き継ぎ元 read を許可済。
 * 引き継ぎ元 UID が claim 上限 (MRU 10) 超で read 拒否されても per-read で握り潰す (未回答扱いに縮退)。
 */

export type MyResponseIdsState =
  | { status: 'loading'; ids: Set<string>; error: null }
  | { status: 'ready'; ids: Set<string>; error: null }
  | { status: 'error'; ids: Set<string>; error: Error };

export function useMyResponseIds(
  districtId: string,
  surveyIds: string[],
  uid: string | null,
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群。省略時は uid 単独 (後方互換)。 */
  selfUids?: string[],
): MyResponseIdsState {
  const [state, setState] = useState<MyResponseIdsState>({
    status: 'loading',
    ids: new Set<string>(),
    error: null,
  });
  // 回答済み判定に使う UID 群: selfUids 指定時はそれ、無ければ uid 単独。
  const uids = selfUids && selfUids.length > 0 ? selfUids : uid ? [uid] : [];
  // array は join で comparable な dependency key を作る (Set でも安定列挙)。
  const key = surveyIds.join('|');
  const uidsKey = uids.join('|');

  useEffect(() => {
    if (uids.length === 0 || surveyIds.length === 0) {
      setState({ status: 'ready', ids: new Set<string>(), error: null });
      return;
    }
    let cancelled = false;
    // 引き継ぎ元 UID の read が claim 上限超で拒否されても全体を error にしないよう per-read で握り潰す。
    const existsForAnyUid = async (sid: string): Promise<boolean> => {
      const checks = await Promise.all(
        uids.map(async (u) => {
          try {
            const snap = await getDoc(
              doc(db, 'districts', districtId, 'surveys', sid, 'responses', u),
            );
            return snap.exists();
          } catch {
            return false;
          }
        }),
      );
      return checks.some((v) => v);
    };
    (async () => {
      try {
        const results = await Promise.all(
          surveyIds.map(async (sid) => ((await existsForAnyUid(sid)) ? sid : null)),
        );
        if (cancelled) return;
        const set = new Set<string>(results.filter((v): v is string => v !== null));
        setState({ status: 'ready', ids: set, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', ids: new Set<string>(), error: e as Error });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, key, uidsKey]);

  return state;
}
